import {
  ComplianceDocumentStatus,
  ComplianceStatus,
  ConstructionStage,
  LongLeadEquipmentStatus,
  NoticeToProceedStatus,
} from '@/types/compliance';
import {
  BatteryConfiguration,
  ChargeRestriction,
  ChargingSource,
  OFFER_STATUS,
  PRODUCT_TYPE,
  PROJECT_OPERATIONAL_STATUS,
  Side,
  StorageTechnology,
  TECHNOLOGY_TYPE,
} from '@/utils/enums';

import { baseApi } from './baseApi';
import { OfferBasicDetails } from './offerApi';
import { ApiTags, OfferTagIds, ProjectTagIds } from './tags';

export type ProjectCompliance = {
  bopAgreementId: ComplianceDocumentStatus | null;
  bopAgreementName: string | null;
  constructionAgreementId: ComplianceDocumentStatus | null;
  constructionAgreementName: string | null;
  energyManagementAgreementId: ComplianceDocumentStatus | null;
  energyManagementAgreementName: string | null;
  epcAgreementId: ComplianceDocumentStatus | null;
  epcAgreementName: string | null;
  facilitiesStudyId: ComplianceDocumentStatus | null;
  facilitiesStudyName: string | null;
  feasibilityStudyId: ComplianceDocumentStatus | null;
  feasibilityStudyName: string | null;
  generationEquipmentSupplyAgreementId: ComplianceDocumentStatus | null;
  generationEquipmentSupplyAgreementName: string | null;
  interconnectionAgreementId: ComplianceDocumentStatus | null;
  interconnectionAgreementName: string | null;
  managementServicesAgreementId: ComplianceDocumentStatus | null;
  managementServicesAgreementName: string | null;
  operationMaintenanceAgreementId: ComplianceDocumentStatus | null;
  operationMaintenanceAgreementName: string | null;
  systemImpactStudyId: ComplianceDocumentStatus | null;
  systemImpactStudyName: string | null;
  certifiedLandTitleSurveyId: ComplianceStatus | null;
  certifiedLandTitleSurveyName: string | null;
  culturalResourcesStudyReviewId: ComplianceDocumentStatus | null;
  culturalResourcesStudyReviewName: string | null;
  femaFloodHazardDeterminationId: ComplianceStatus | null;
  femaFloodHazardDeterminationName: string | null;
  landControlAgreementsId: ComplianceDocumentStatus | null;
  landControlAgreementsName: string | null;
  litigationDisclosureId: ComplianceDocumentStatus | null;
  litigationDisclosureName: string | null;
  phaseEnvironmentalReviewId: ComplianceDocumentStatus | null;
  phaseEnvironmentalReviewName: string | null;
  primaryCountyPermitId: ComplianceStatus | null;
  primaryCountyPermitName: string | null;
  primaryStatePermitId: ComplianceStatus | null;
  primaryStatePermitName: string | null;
  primaryTownshipPermitId: ComplianceStatus | null;
  primaryTownshipPermitName: string | null;
  taxAbatementApplicationId: ComplianceStatus | null;
  taxAbatementApplicationName: string | null;
  threatenedEndangeredSpeciesReviewId: ComplianceDocumentStatus | null;
  threatenedEndangeredSpeciesReviewName: string | null;
  titleInsuranceId: ComplianceStatus | null;
  titleInsuranceName: string | null;
  wetlandsDelineationReviewId: ComplianceDocumentStatus | null;
  wetlandsDelineationReviewName: string | null;
  dnhFromFaaId: ComplianceStatus | null;
  dnhFromFaaName: string | null;
  energyResourceStudyId: ComplianceDocumentStatus | null;
  energyResourceStudyName: string | null;
  independentEngineeringReportId: ComplianceDocumentStatus | null;
  independentEngineeringReportName: string | null;
  insuranceConsultantReportId: ComplianceDocumentStatus | null;
  insuranceConsultantReportName: string | null;
  transmissionCongestionStudyId: ComplianceDocumentStatus | null;
  transmissionCongestionStudyName: string | null;
  // Battery new-build statuses. Null for Wind and Solar.
  batterySupplyAgreementId: ComplianceDocumentStatus | null;
  batterySupplyAgreementName: string | null;
  constructionStageId: ConstructionStage | null;
  constructionStageName: string | null;
  fullNoticeToProceedId: NoticeToProceedStatus | null;
  fullNoticeToProceedName: string | null;
  fireCodeAndAhjApprovalId: ComplianceStatus | null;
  fireCodeAndAhjApprovalName: string | null;
  longLeadEquipmentSecuredId: LongLeadEquipmentStatus | null;
  longLeadEquipmentSecuredName: string | null;
};

/**
 * A `BigDecimal` in a response. The backend serializes it as a JSON number, but
 * older frontend types call such fields strings, so both are accepted.
 */
type ResponseDecimal = number | string | null;

/** An `{ id, name }` reference pair, as the reference endpoints return. */
export type ChargeRestrictionReference = {
  id: ChargeRestriction;
  name: string;
};

/**
 * A project at the other end of a co-location link (`CoLocatedProjectResponse`).
 * On a battery it is the paired generator; on a generator it is a co-located
 * battery. Shaped for the viewer: masked as the linked project's own detail page
 * would be, with `name` a placeholder such as `[Wind Project]`. `relationshipId`
 * is only for the viewed project's owner or a platform admin. `projectId` is null
 * only for an owner whose linked project has left its corporate group. When
 * masked, `companyId` is null and `owner` is `--`. The battery fields, from
 * `batteryConfigurationId` to `storageTechnologyName`, are null on a generator
 * entry.
 */
export type CoLocatedProject = {
  relationshipId: string | null;
  projectId: string | null;
  companyId: string | null;
  name: string;
  owner: string | null;
  technologyId: string;
  technologyName: string;
  megawattAc: ResponseDecimal;
  projectOperationalStatusId: string;
  projectOperationalStatusName: string;
  batteryConfigurationId: string | null;
  batteryConfigurationName: string | null;
  chargingSourceId: string | null;
  chargingSourceName: string | null;
  energyCapacityMegawattHour: ResponseDecimal;
  durationHours: ResponseDecimal;
  storageTechnologyId: string | null;
  storageTechnologyName: string | null;
};

/**
 * A corporate-group project a battery can be paired with
 * (`CoLocationCandidateResponse`), from
 * `GET /projects/co-location-candidates`. `pairedGenerator` is a battery
 * candidate's off-platform generator, which linking overwrites. It is null for a
 * generator candidate and for a battery that describes no generator.
 */
export type CoLocationCandidate = {
  id: string;
  name: string;
  owner: string | null;
  technologyId: string;
  technologyName: string;
  isDraft: boolean;
  pairedGenerator: {
    name: string | null;
    technologyId: string | null;
    technologyName: string | null;
    megawattAc: ResponseDecimal;
    projectOperationalStatusId: string | null;
    projectOperationalStatusName: string | null;
  } | null;
};

export type ProjectInfo = {
  id: string;
  companyId: string;
  name: string;
  legalName: string | null;
  owner: string | null;
  technologyId: string;
  technologyName: string;
  nameplateCapacity: number | null;
  isoId: string | null;
  isoName: string | null;
  projectOperationalStatusId: string;
  projectOperationalStatusName: string;
  cod: string | null;
  targetCod: string | null;
  isDraft: boolean;
  createdAt: string;
  updatedAt: string;
};

export type ProjectDetails = {
  id: string;
  companyId: string;
  name: string;
  legalName: string | null;
  owner: string | null;
  operator: string | null;
  technologyId: string;
  technologyName: string;
  projectOperationalStatusId: PROJECT_OPERATIONAL_STATUS;
  projectOperationalStatusName: string;
  megawattAc: string | null;
  megawattDc: string | null;
  availableCapacityAc: string | null;
  panelTypeId: string | null;
  panelTypeName: string | null;
  panelTechnologyId: string | null;
  panelTechnologyName: string | null;
  panelMakeAndModel: string | null;
  trackingTypeId: string | null;
  trackingTypeName: string | null;
  p50CapacityFactor: string | null;
  turbineMakeAndModel: string | null;
  turbineCount: string | null;
  turbineMegawatt: string | null;
  hubHeight: string | null;
  turbineDiameter: string | null;
  // Battery fields. Null for Wind and Solar. Power capacity is megawattAc.
  batteryConfigurationId: BatteryConfiguration | null;
  batteryConfigurationName: string | null;
  chargingSourceId: ChargingSource | null;
  chargingSourceName: string | null;
  /** Derived from the generator link, for every viewer. Null unless the project is a co-located battery. */
  pairedGeneratorOnPlatform: boolean | null;
  pairedGeneratorName: string | null;
  pairedGeneratorTechnologyId: TECHNOLOGY_TYPE | null;
  pairedGeneratorTechnologyName: string | null;
  pairedGeneratorMegawattAc: ResponseDecimal;
  pairedGeneratorOperationalStatusId: PROJECT_OPERATIONAL_STATUS | null;
  pairedGeneratorOperationalStatusName: string | null;
  sharedPointOfInterconnection: boolean | null;
  siteExportLimitMegawatt: ResponseDecimal;
  energyCapacityMegawattHour: ResponseDecimal;
  /** Energy ÷ power, 2 decimals half up. Response only; never sent. */
  durationHours: ResponseDecimal;
  roundTripEfficiency: ResponseDecimal;
  /** Two-decimal text such as `"90.00"`, or `"--"` when masked. */
  maxChargeRateMegawatt: string | null;
  /** Two-decimal text such as `"90.00"`, or `"--"` when masked. */
  maxDischargeRateMegawatt: string | null;
  usableSocMax: ResponseDecimal;
  warrantedCyclesPerYear: number | null;
  /** Derived from the window and limit values; `NONE` when neither is stored. */
  chargeRestrictions: ChargeRestrictionReference[] | null;
  /** `HH:mm:ss`, zone-less local time. */
  prohibitedChargingStart: string | null;
  /** `HH:mm:ss`, zone-less local time. */
  prohibitedChargingEnd: string | null;
  chargingLimitMegawatt: ResponseDecimal;
  storageTechnologyId: StorageTechnology | null;
  storageTechnologyName: string | null;
  coLocatedProjects: CoLocatedProject[] | null;
  cityName: string | null;
  countyId: string | null;
  countyName: string | null;
  stateId: string | null;
  stateName: string | null;
  countryId: string | null;
  countryName: string | null;
  latitude: string | null;
  longitude: string | null;
  isoId: string | null;
  isoName: string | null;
  interconnectionPointId: string | null;
  interconnectionPointName: string | null;
  interconnectionRegionId: string | null;
  interconnectionVoltageId: string | null;
  interconnectionVoltageName: string | null;
  interconnectingUtility: string | null;
  interconnectionQueueId: string | null;
  cod: string | null;
  targetCod: string | null;
  guaranteedCod: string | null;
  projectComplianceResponseDto: ProjectCompliance | null;
  isDraft: boolean;
  createdBy: string;
  updatedBy: string | null;
  publishedBy: string | null;
  published: string | null;
  created: string;
  updated: string | null;
};

export type GetProjectsParams = {
  companyId: string;
  draft?: boolean;
};

export type GetProjectsByIdParams = {
  companyId: string;
  projectId: string;
  side: Side;
};

export type GetCoLocationCandidatesParams = {
  companyId: string;
  /** The technology of the project being paired; `BATTERY` for the battery form. */
  technology: TECHNOLOGY_TYPE;
};

export type GetProjectOffersParams = {
  projectId: string;
  companyId: string;
  sideView: Side;
  statuses?: OFFER_STATUS[];
  productType?: PRODUCT_TYPE;
};

export interface ComplianceRequestDto {
  bopAgreement: ComplianceDocumentStatus | null;
  constructionAgreement: ComplianceDocumentStatus | null;
  energyManagementAgreement: ComplianceDocumentStatus | null;
  epcAgreement: ComplianceDocumentStatus | null;
  facilitiesStudy: ComplianceDocumentStatus | null;
  feasibilityStudy: ComplianceDocumentStatus | null;
  generationEquipmentSupplyAgreement: ComplianceDocumentStatus | null;
  interconnectionAgreement: ComplianceDocumentStatus | null;
  managementServicesAgreement: ComplianceDocumentStatus | null;
  operationMaintenanceAgreement: ComplianceDocumentStatus | null;
  systemImpactStudy: ComplianceDocumentStatus | null;
  certifiedLandTitleSurvey: ComplianceStatus | null;
  culturalResourcesStudyReview: ComplianceDocumentStatus | null;
  femaFloodHazardDetermination: ComplianceStatus | null;
  landControlAgreements: ComplianceDocumentStatus | null;
  litigationDisclosure: ComplianceDocumentStatus | null;
  phaseEnvironmentalReview: ComplianceDocumentStatus | null;
  primaryCountyPermit: ComplianceStatus | null;
  primaryStatePermit: ComplianceStatus | null;
  primaryTownshipPermit: ComplianceStatus | null;
  taxAbatementApplication: ComplianceStatus | null;
  threatenedEndangeredSpeciesReview: ComplianceDocumentStatus | null;
  titleInsurance: ComplianceStatus | null;
  wetlandsDelineationReview: ComplianceDocumentStatus | null;
  dnhFromFaa: ComplianceStatus | null;
  energyResourceStudy: ComplianceDocumentStatus | null;
  independentEngineeringReport: ComplianceDocumentStatus | null;
  insuranceConsultantReport: ComplianceDocumentStatus | null;
  transmissionCongestionStudy: ComplianceDocumentStatus | null;
  // Battery new-build statuses. Sent only for a battery.
  batterySupplyAgreement?: ComplianceDocumentStatus | null;
  constructionStage?: ConstructionStage | null;
  fullNoticeToProceed?: NoticeToProceedStatus | null;
  fireCodeAndAhjApproval?: ComplianceStatus | null;
  longLeadEquipmentSecured?: LongLeadEquipmentStatus | null;
}

/**
 * Battery request fields shared by create and update, matching
 * `CreateProjectRequestDto`. All optional: Wind, Solar and drafts leave them out.
 * `usableSocMin` and duration are deliberately absent; the form sends neither.
 */
interface BatteryProjectRequestFields {
  batteryConfiguration?: BatteryConfiguration;
  chargingSource?: ChargingSource;
  /** The linked generator project. Null means the generator is not on CleanTrade. */
  pairedProjectId?: string | null;
  pairedGeneratorName?: string;
  pairedGeneratorTechnology?: TECHNOLOGY_TYPE.WIND | TECHNOLOGY_TYPE.SOLAR;
  pairedGeneratorMegawattAc?: string;
  pairedGeneratorOperationalStatus?: PROJECT_OPERATIONAL_STATUS;
  sharedPointOfInterconnection?: boolean;
  siteExportLimitMegawatt?: string;
  energyCapacityMegawattHour?: string;
  roundTripEfficiency?: string;
  maxChargeRateMegawatt?: string;
  maxDischargeRateMegawatt?: string;
  usableSocMax?: string;
  warrantedCyclesPerYear?: string;
  /** `HH:mm`, zone-less local time. */
  prohibitedChargingStart?: string;
  /** `HH:mm`, zone-less local time. */
  prohibitedChargingEnd?: string;
  chargingLimitMegawatt?: string;
  storageTechnology?: StorageTechnology;
}

export interface CreateProjectRequest extends BatteryProjectRequestFields {
  name: string;
  legalName: string;
  owner: string;
  operator: string;
  technology: string;
  projectOperationalStatus: string;
  megawattAc: string;
  megawattDc?: string;
  /** Not sent for a battery, which carries a Contracted Capacity on its offers instead. */
  availableCapacityAc?: string;
  panelType?: string;
  panelTechnology?: string;
  panelMakeAndModel?: string;
  trackingType?: string;
  p50CapacityFactor?: string;
  turbineMakeAndModel?: string;
  turbineCount?: string;
  turbineMegawatt?: string;
  hubHeight?: string;
  turbineDiameter?: string;
  cityName?: string;
  countyId: string;
  stateId: string;
  countryId: string;
  latitude: string;
  longitude: string;
  iso: string;
  interconnectionPointId: string;
  interconnectionRegionId: string;
  interconnectionVoltage: string;
  interconnectingUtility: string;
  interconnectionQueueId?: string;
  cod?: string;
  targetCod?: string;
  guaranteedCod?: string;
  complianceRequestDto?: ComplianceRequestDto;
  isDraft: boolean;
}

export interface UpdateProjectRequest extends BatteryProjectRequestFields {
  name: string;
  operator: string;
  megawattAc: string;
  /** Not sent for a battery, which carries a Contracted Capacity on its offers instead. */
  availableCapacityAc?: string;
  cityName?: string;
  countyId: string;
  latitude: string;
  longitude: string;
  iso: string;
  interconnectionPointId: string;
  interconnectionRegionId: string;
  interconnectionVoltage: string;
  interconnectingUtility: string;
  megawattDc?: string;
  panelType?: string;
  panelTechnology?: string;
  panelMakeAndModel?: string;
  trackingType?: string;
  p50CapacityFactor?: string;
  turbineMakeAndModel?: string;
  turbineCount?: string;
  turbineMegawatt?: string;
  hubHeight?: string;
  turbineDiameter?: string;
  interconnectionQueueId?: string;
  cod?: string;
  targetCod?: string;
  guaranteedCod?: string;
  complianceRequestDto?: ComplianceRequestDto;
  isDraft: boolean;
}

export type CreateProjectParams = {
  data: CreateProjectRequest;
  companyId: string;
};

export type UpdateProjectParams = {
  data: UpdateProjectRequest;
  companyId: string;
  projectId: string;
};

export type PublishProjectParams = {
  projectId: string;
  companyId: string;
};

export type UpdateAndPublishProjectParams = {
  data: UpdateProjectRequest;
  projectId: string;
  companyId: string;
};

const BASE_URL = '/api/v1/projects';

export const projectApi = baseApi.injectEndpoints({
  endpoints: build => ({
    getProjects: build.query<ProjectInfo[], GetProjectsParams>({
      query: ({ companyId, draft = false }) => ({
        url: BASE_URL,
        params: { draft },
        headers: { 'X-Company-Id': companyId },
      }),
      providesTags: (result, error, { draft }) => [
        {
          type: ApiTags.PROJECTS,
          id: draft ? ProjectTagIds.DRAFT : ProjectTagIds.ACTIVE,
        },
      ],
    }),
    getProjectById: build.query<ProjectDetails, GetProjectsByIdParams>({
      query: ({ companyId, projectId, side }) => ({
        url: `${BASE_URL}/${projectId}`,
        headers: { 'X-Company-Id': companyId, 'X-Side-View': side },
      }),
      providesTags: (result, error, { projectId }) => [
        {
          type: ApiTags.PROJECTS,
          id: projectId,
        },
      ],
    }),
    // The backend leaves drafts out (a draft cannot be linked). The battery form still drops
    // any `isDraft: true` entry as a guard.
    getCoLocationCandidates: build.query<
      CoLocationCandidate[],
      GetCoLocationCandidatesParams
    >({
      query: ({ companyId, technology }) => ({
        url: `${BASE_URL}/co-location-candidates`,
        params: { technology },
        headers: { 'X-Company-Id': companyId },
      }),
      providesTags: [
        { type: ApiTags.PROJECTS, id: ProjectTagIds.CO_LOCATION_CANDIDATES },
      ],
    }),
    getProjectOffers: build.query<OfferBasicDetails[], GetProjectOffersParams>({
      query: ({ projectId, companyId, sideView, statuses, productType }) => {
        const searchParams = new URLSearchParams();

        if (statuses) {
          statuses.forEach(status => searchParams.append('statuses', status));
        }
        if (productType) {
          searchParams.append('productType', productType);
        }

        const queryString = searchParams.toString();
        const url = `${BASE_URL}/${projectId}/offers${
          queryString ? `?${queryString}` : ''
        }`;

        return {
          url,
          headers: {
            'X-Company-Id': companyId,
            'X-Side-View': sideView,
          },
        };
      },
      providesTags: (result, error, { projectId, companyId }) => [
        {
          type: ApiTags.OFFERS,
          id: `${OfferTagIds.PROJECT_OFFERS}-${projectId}-${companyId}`,
        },
      ],
    }),
    deleteProject: build.mutation<
      void,
      { id: string; companyId: string; draft: boolean }
    >({
      query: ({ id, companyId }) => ({
        url: `${BASE_URL}/${id}`,
        method: 'DELETE',
        headers: { 'X-Company-Id': companyId },
      }),
      invalidatesTags: (result, error, { draft = false }) => [
        {
          type: ApiTags.PROJECTS,
          id: draft ? ProjectTagIds.DRAFT : ProjectTagIds.ACTIVE,
        },
        { type: ApiTags.PROJECTS, id: ProjectTagIds.CO_LOCATION_CANDIDATES },
      ],
    }),
    createProject: build.mutation<ProjectDetails, CreateProjectParams>({
      query: ({ data, companyId }) => ({
        url: BASE_URL,
        method: 'POST',
        body: data,
        headers: { 'X-Company-Id': companyId },
      }),
      invalidatesTags: (result, error, { data }) => [
        {
          type: ApiTags.PROJECTS,
          id: data.isDraft ? ProjectTagIds.DRAFT : ProjectTagIds.ACTIVE,
        },
        { type: ApiTags.PROJECTS, id: ProjectTagIds.CO_LOCATION_CANDIDATES },
      ],
    }),

    updateProject: build.mutation<ProjectDetails, UpdateProjectParams>({
      query: ({ data, companyId, projectId }) => ({
        url: `${BASE_URL}/${projectId}`,
        method: 'PUT',
        body: data,
        headers: { 'X-Company-Id': companyId },
      }),
      invalidatesTags: [{ type: ApiTags.PROJECTS }],
    }),

    publishProject: build.mutation<ProjectDetails, PublishProjectParams>({
      query: ({ projectId, companyId }) => ({
        url: `${BASE_URL}/${projectId}`,
        method: 'PATCH',
        headers: { 'X-Company-Id': companyId },
      }),
      invalidatesTags: [{ type: ApiTags.PROJECTS }],
    }),

    updateAndPublishProject: build.mutation<
      ProjectDetails,
      UpdateAndPublishProjectParams
    >({
      async queryFn(
        { data, projectId, companyId },
        { dispatch },
        _extraOptions,
        fetchWithBQ
      ) {
        // Update project
        const updateResult = await fetchWithBQ({
          url: `${BASE_URL}/${projectId}`,
          method: 'PUT',
          body: data,
          headers: { 'X-Company-Id': companyId },
        });

        if (updateResult.error) {
          return { error: updateResult.error };
        }

        // Publish project only if update succeeds
        const publishResult = await fetchWithBQ({
          url: `${BASE_URL}/${projectId}`,
          method: 'PATCH',
          headers: { 'X-Company-Id': companyId },
        });

        if (publishResult.error) {
          return { error: publishResult.error };
        }

        // Invalidate tags only after both succeed
        dispatch(baseApi.util.invalidateTags([{ type: ApiTags.PROJECTS }]));

        return { data: publishResult.data as ProjectDetails };
      },
    }),
  }),
});

export const {
  useGetProjectsQuery,
  useGetProjectByIdQuery,
  useGetCoLocationCandidatesQuery,
  useGetProjectOffersQuery,
  useDeleteProjectMutation,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  usePublishProjectMutation,
  useUpdateAndPublishProjectMutation,
} = projectApi;
